import { ReactNode, useState, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'

interface TooltipProps {
  children: ReactNode
  content: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  multiline?: boolean
  className?: string
}

export default function Tooltip({ children, content, position = 'top', multiline = false, className = '' }: TooltipProps) {
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)

  const OFFSET = 8

  const calcCoords = useCallback(() => {
    if (!anchorRef.current) return
    const r = anchorRef.current.getBoundingClientRect()
    let top = 0
    let left = 0
    if (position === 'top') {
      top = r.top - OFFSET
      left = r.left + r.width / 2
    } else if (position === 'bottom') {
      top = r.bottom + OFFSET
      left = r.left + r.width / 2
    } else if (position === 'left') {
      top = r.top + r.height / 2
      left = r.left - OFFSET
    } else {
      top = r.top + r.height / 2
      left = r.right + OFFSET
    }
    setCoords({ top, left })
  }, [position])

  const hide = useCallback(() => setCoords(null), [])

  const transformClass =
    position === 'top' ? '-translate-x-1/2 -translate-y-full' :
    position === 'bottom' ? '-translate-x-1/2' :
    position === 'left' ? '-translate-x-full -translate-y-1/2' :
    '-translate-y-1/2'

  const wrapClass = multiline
    ? 'whitespace-pre-wrap max-w-xs text-left leading-snug'
    : 'whitespace-nowrap'

  return (
    <div
      ref={anchorRef}
      className="relative inline-block"
      onMouseEnter={() => { calcCoords(); }}
      onMouseLeave={hide}
      onPointerDown={hide}
      onFocus={() => { calcCoords(); }}
      onBlur={hide}
    >
      {/* Disabled buttons skip hit-testing in Chromium — without this, the wrapper never sees hover. */}
      <div className="inline-block [&_button:disabled]:pointer-events-none">{children}</div>

      {coords && content && createPortal(
        <div
          className={`fixed z-[9999] px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-sm pointer-events-none ${wrapClass} ${transformClass} ${className}`}
          style={{ top: coords.top, left: coords.left }}
          role="tooltip"
        >
          {content}
        </div>,
        document.body,
      )}
    </div>
  )
}
